/**
 * Per-route prefetch hooks for the TabBar.
 *
 * On `onPointerDown` of a tab, we kick off the destination page's
 * primary queries before React Router actually mounts the new route.
 * Touching the screen typically precedes the navigation event by
 * ~80–150 ms, which is enough headroom for a 4G API round-trip to
 * land before the page renders — so the destination shows real data
 * on first paint instead of a "Loading…" flash.
 *
 * Each entry uses the same `queryOptions()` factory the destination
 * page reads from, so the prefetched response lands in exactly the
 * cache slot the page's `useQuery` will hit.
 */
import type { QueryClient } from '@tanstack/react-query';
import {
  memoriesListQueryOptions,
  myCirclesQueryOptions,
  memoryVerseThisWeekQueryOptions,
  prayersQueryOptions,
  readingPlanMeQueryOptions,
  todayQueryOptions,
  videosQueryOptions,
} from './queries';

type Prefetch = (qc: QueryClient) => void;

const PREFETCH: Record<string, Prefetch> = {
  '/today': (qc) => {
    void qc.prefetchQuery(todayQueryOptions());
  },
  '/memories': (qc) => {
    void qc.prefetchQuery(memoriesListQueryOptions());
  },
  '/generations': (qc) => {
    void qc.prefetchQuery(myCirclesQueryOptions());
  },
  '/walk': (qc) => {
    void qc.prefetchQuery(memoryVerseThisWeekQueryOptions());
    void qc.prefetchQuery(readingPlanMeQueryOptions());
    void qc.prefetchQuery(videosQueryOptions());
    void qc.prefetchQuery(prayersQueryOptions('active'));
  },
  // /chat is intentionally omitted — history hydrates over a WebSocket
  // upgrade, not a REST query, so there is nothing to warm up.
};

export function prefetchForPath(qc: QueryClient, pathname: string): void {
  const fn = PREFETCH[pathname];
  if (fn) fn(qc);
}
